import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'react-hot-toast'
import { AuthProvider } from './hooks/useAuth'
import { ThemeProvider } from './contexts/ThemeContext'
import ProtectedRoute from './components/ProtectedRoute'
import MainLayout from './components/MainLayout'

// Pages
import Dashboard from './pages/Dashboard'
import Login from './pages/Login'
import Employees from './pages/Employees'
import AddEmployee from './pages/AddEmployee'
import Payroll from './pages/Payroll'
import Leaves from './pages/Leaves'
import Loans from './pages/Loans'
import AdminCredentials from './pages/AdminCredentialsEnhanced'
import AdminSettings from './pages/AdminSettings'
import MyPayslips from './pages/MyPayslips'
import Reports from './pages/Reports'
import Profile from './pages/Profile'
import LeaveTypes from "./pages/LeaveTypes"
import ExchangeRates from './pages/ExchangeRates' 
import EmployeeProfiles from './pages/employees/profiles'
import LeaveCalendarPage from './pages/leaves/calendar'
import LeaveRequestsPage from './pages/LeaveRequestsPage'
import AdminLogs from './pages/AdminLogs' // ← AJOUTEZ CET IMPORT
import PayrollHistory from './pages/PayrollHistory'
import AllLoans from './pages/AllLoans'
import LoanApplications from './pages/LoanApplications'
import LoanTypesPage from './pages/loans/types'
import HRAnalytics from './pages/reports/HRAnalytics'
import PayrollReports from './pages/reports/PayrollReports'
import LeaveReports from './pages/reports/LeaveReports'
import EmployeeReports from './pages/reports/EmployeeReports'
import AllPayslips from './pages/AllPayslips'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
      retry: (failureCount, error: any) => {
        if (error?.response?.status === 401 || error?.response?.status === 403) return false
        return failureCount < 3
      },
    },
    mutations: { retry: false },
  },
})

// Wrapper component for protected routes with layout
const ProtectedLayout = ({ children, requiredRoles }: { children: React.ReactNode; requiredRoles: string[] }) => (
  <ProtectedRoute requiredRoles={requiredRoles}>
    <MainLayout>
      {children}
    </MainLayout>
  </ProtectedRoute>
);

function App(): JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <Router>
          <AuthProvider>
            <Routes>
              {/* 🔓 Public Routes */}
              <Route path="/login" element={<Login />} />

              {/* 🧭 Protected Routes with Layout */}
              <Route
                path="/"
                element={
                  <ProtectedLayout requiredRoles={['admin', 'hr_manager', 'hr_staff', 'employee']}>
                    <Dashboard />
                  </ProtectedLayout>
                }
              />

              <Route
                path="/profile"
                element={
                  <ProtectedLayout requiredRoles={['admin', 'hr_manager', 'hr_staff', 'employee']}>
                    <Profile />
                  </ProtectedLayout>
                }
              />

              {/* 👨‍💼 Admin / HR only */}
              <Route
                path="/employees"
                element={
                  <ProtectedLayout requiredRoles={['admin', 'hr_manager', 'hr_staff']}>
                    <Employees />
                  </ProtectedLayout>
                }
              />

              <Route
                path="/employees/add"
                element={
                  <ProtectedLayout requiredRoles={['admin', 'hr_manager']}>
                    <AddEmployee />
                  </ProtectedLayout>
                }
              />

              <Route
                  path="/employees/profiles"
                  element={
                    <ProtectedLayout requiredRoles={['admin', 'hr_manager', 'hr_staff']}>
                      <EmployeeProfiles />
                    </ProtectedLayout>
                  }
              />

              <Route
                path="/payroll/all-payslips"
                element={
                  <ProtectedLayout requiredRoles={['admin', 'hr_manager', 'hr_staff']}>
                    <AllPayslips />
                  </ProtectedLayout>
                }
              />

              <Route
                path="/payroll"
                element={
                  <ProtectedLayout requiredRoles={['admin', 'hr_manager', 'hr_staff']}>
                    <Payroll />
                  </ProtectedLayout>
                }
              />

              <Route
                path="/payroll/history"
                element={
                  <ProtectedLayout requiredRoles={['admin', 'hr_manager', 'hr_staff']}>
                    <PayrollHistory />
                  </ProtectedLayout>
                }
              />

              <Route
                path="/reports"
                element={
                  <ProtectedLayout requiredRoles={['admin', 'hr_manager']}>
                    <Reports />
                  </ProtectedLayout>
                }
              />

              {/* 📊 Reports & Analytics */}
              <Route
                path="/reports/analytics"
                element={
                  <ProtectedLayout requiredRoles={['admin', 'hr_manager']}>
                    <HRAnalytics />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/reports/payroll"
                element={
                  <ProtectedLayout requiredRoles={['admin', 'hr_manager']}>
                    <PayrollReports />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/reports/leaves"
                element={
                  <ProtectedLayout requiredRoles={['admin', 'hr_manager']}>
                    <LeaveReports />
                  </ProtectedLayout>
                }
              />
              <Route
                path="/reports/employees"
                element={
                  <ProtectedLayout requiredRoles={['admin', 'hr_manager']}>
                    <EmployeeReports />
                  </ProtectedLayout>
                }
              />

              <Route
                path="/leave-types"
                element={
                  <ProtectedLayout requiredRoles={['admin', 'hr_manager']}>
                    <LeaveTypes />
                  </ProtectedLayout>
                }
              />

              <Route
                path="/leaves/calendar"
                element={
                  <ProtectedLayout requiredRoles={['admin', 'hr_manager', 'hr_staff', 'manager', 'employee']}>
                    <LeaveCalendarPage />
                  </ProtectedLayout>
                }
              />

              <Route
                path="/leaves/all"
                element={
                  <ProtectedLayout requiredRoles={['admin', 'hr_manager', 'hr_staff', 'manager']}>
                    <LeaveRequestsPage />
                  </ProtectedLayout>
                }
              />

              <Route
                path="/admin/settings"
                element={
                  <ProtectedLayout requiredRoles={['admin']}>
                    <AdminSettings />
                  </ProtectedLayout>
                }
              />

              <Route
                path="/admin/credentials"
                element={
                  <ProtectedLayout requiredRoles={['admin']}>
                    <AdminCredentials />
                  </ProtectedLayout>
                }
              />

              {/* AJOUTEZ CETTE NOUVELLE ROUTE POUR LES LOGS */}
              <Route
                path="/admin/logs"
                element={
                  <ProtectedLayout requiredRoles={['admin']}>
                    <AdminLogs />
                  </ProtectedLayout>
                }
              />

              {/* 👨‍🏭 HR + Employés */}
              <Route
                path="/leaves"
                element={
                  <ProtectedLayout requiredRoles={['admin', 'hr_manager', 'hr_staff', 'employee']}>
                    <Leaves />
                  </ProtectedLayout>
                }
              />

              <Route
                path="/loans"
                element={
                  <ProtectedLayout requiredRoles={['admin', 'hr_manager', 'hr_staff', 'employee']}>
                    <Loans />
                  </ProtectedLayout>
                }
              />

              <Route
                path="/loans/all"
                element={
                  <ProtectedLayout requiredRoles={['admin', 'hr_manager', 'hr_staff']}>
                    <AllLoans />
                  </ProtectedLayout>
                }
              />

              <Route
                path="/loans/applications"
                element={
                  <ProtectedLayout requiredRoles={['admin', 'hr_manager', 'hr_staff', 'manager']}>
                    <LoanApplications />
                  </ProtectedLayout>
                }
              />

              <Route
                path="/loans/types"
                element={
                  <ProtectedLayout requiredRoles={['admin', 'hr_manager']}>
                    <LoanTypesPage />
                  </ProtectedLayout>
                }
              />

              {/* 👤 Employé uniquement */}
              <Route
                path="/my-payslips"
                element={
                  <ProtectedLayout requiredRoles={['employee']}>
                    <MyPayslips />
                  </ProtectedLayout>
                }
              />

              {/* 💰 Exchange Rates */}
              <Route
                path="/exchange-rates"
                element={
                  <ProtectedLayout requiredRoles={['admin', 'hr_manager', 'hr_staff', 'employee']}>
                    <ExchangeRates />
                  </ProtectedLayout>
                }
              />

              {/* ❌ 404 */}
              <Route
                path="*"
                element={
                  <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                    <div className="text-center">
                      <h1 className="text-6xl font-bold text-gray-400 dark:text-gray-600">404</h1>
                      <p className="text-xl text-gray-600 dark:text-gray-400 mt-4">Page Not Found</p>
                      <button
                        onClick={() => window.history.back()}
                        className="mt-6 px-4 py-2 bg-[#3b82f6] text-white rounded-md hover:bg-[#2563eb] transition-colors"
                      >
                        Go Back
                      </button>
                    </div>
                  </div>
                }
              />
            </Routes>

            {/* 🔔 Toasts */}
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: { background: '#363636', color: '#fff' },
                success: { style: { background: '#10B981', color: '#fff' } },
                error: { style: { background: '#EF4444', color: '#fff' } },
              }}
            />
          </AuthProvider>
        </Router>
      </ThemeProvider>
    </QueryClientProvider>
  )
}

export default App